import React, { useMemo } from "react";
import { Activity, Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import NCAAFMobileGameCard from "./NCAAFMobileGameCard";
import { EmptyState } from "../../../../../components/shared/loading/EmptyState";
import { LoadingSpinner } from "../../../../../components/shared/loading/LoadingSpinner";

const MobileNCAAFGamesView = ({
  games,
  loading,
  pagination,
  onPageChange,
  t,
  isGameSelected,
  isGameFrozen,
  onToggleSelection,
  getGamePrediction,
  onViewOdds,
  onViewPrediction
}) => {
  const paginatedGames = useMemo(() => {
    if (!pagination) return games;
    const startIndex = (pagination.currentPage - 1) * pagination.perPage;
    return games.slice(startIndex, startIndex + pagination.perPage);
  }, [games, pagination]);

  const handlePageChange = (direction) => {
    if (!pagination) return;
    const nextPage = direction === "next" ? pagination.currentPage + 1 : pagination.currentPage - 1;
    onPageChange(Math.max(1, Math.min(nextPage, pagination.totalPages)));
  };

  return (
    <section className="space-y-4">
      <div className="border border-white/10 bg-white/[0.02]">
        <div className="flex items-center justify-between border-b border-white/5 px-4 py-3">
          <div>
            <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">
              {t("Currently showing")}
            </p>
            <h2 className="text-lg font-black uppercase tracking-tight text-white flex items-center gap-2">
              {t("Games")} ({games.length})
            </h2>
          </div>
          {pagination && pagination.totalPages > 1 && (
            <span className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-200">
              {t("Page")} {pagination.currentPage} {t("of")} {pagination.totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <LoadingSpinner message={t("Loading games...")} />
        ) : games.length === 0 ? (
          <EmptyState
            title={t("No games found")}
            description={t("Try selecting a different date or league")}
            icon={Calendar}
          />
        ) : (
          <div className="space-y-3 p-4">
            {paginatedGames.map((game, index) => {
              const normalizedId = game?.game?.id || game?.id || `game-${index}`;
              const gameWithId = {
                ...game,
                id: normalizedId
              };
              return (
                <NCAAFMobileGameCard
                  key={gameWithId.id}
                  game={gameWithId}
                  isSelected={isGameSelected?.(gameWithId)}
                  isFrozen={isGameFrozen?.(gameWithId.id)}
                  onToggleSelection={() => onToggleSelection?.(gameWithId)}
                  prediction={getGamePrediction ? getGamePrediction(gameWithId.id) : null}
                  showPredictionButton
                  onViewOdds={onViewOdds}
                  onViewPrediction={onViewPrediction}
                />
              );
            })}
          </div>
        )}

        {!loading && games.length > 0 && pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 border-t border-white/5 px-4 py-3">
            <button
              onClick={() => handlePageChange("prev")}
              disabled={pagination.currentPage === 1}
              className="flex items-center gap-1 border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-200 transition-all disabled:opacity-40"
            >
              <ChevronLeft size={14} /> {t("Prev")}
            </button>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-gray-200">
              {pagination.currentPage} / {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange("next")}
              disabled={pagination.currentPage === pagination.totalPages}
              className="flex items-center gap-1 border border-white/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-200 transition-all disabled:opacity-40"
            >
              {t("Next")} <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MobileNCAAFGamesView;
